import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  CustomerFormFields,
  customerFormZ,
  customerFormDefaults,
  toCreateCustomerInput,
  type CustomerFormData,
} from 'src/components/customers/CustomerFormFields';

export interface CreatedCustomer {
  userId: string;
  name: { first: string; last: string };
  email?: string;
  phone?: string;
}

interface CreateCustomerSubviewProps {
  /** Creates the customer and reports it back; the drawer assigns the order to them. */
  onCreate: (input: ReturnType<typeof toCreateCustomerInput>) => void;
  onBack: () => void;
  /** True while either the creation or the assignment that follows it is in flight. */
  isBusy: boolean;
}

/**
 * A subview of the customer drawer, not an overlay. Stacking a second sheet on top of the
 * drawer put two focus traps on screen at once and left the admin unsure which surface the
 * Escape key would close, so creation renders in place and returns to the search it came from.
 */
export const CreateCustomerSubview: FC<CreateCustomerSubviewProps> = ({
  onCreate,
  onBack,
  isBusy,
}) => {
  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormZ),
    defaultValues: customerFormDefaults,
    mode: 'onTouched',
  });

  const onSubmit = (data: CustomerFormData) => {
    if (isBusy) return;
    onCreate(toCreateCustomerInput(data));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-11 px-2 text-sm text-muted hover:text-primary"
          onClick={onBack}
          disabled={isBusy}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to customer search
        </Button>
        <h4 className="text-sm font-semibold text-gray-900">Create and assign customer</h4>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <CustomerFormFields form={form} />

          <Button type="submit" className="w-full" disabled={isBusy}>
            {isBusy ? 'Creating and assigning…' : 'Create and assign customer'}
          </Button>
        </form>
      </Form>
    </div>
  );
};
